import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { setSecondFactorRequiredHandler, setSessionExpiredHandler } from "../lib/api";
import { clearTokens } from "./tokenStore";
import { buildLogoutUrl } from "./oidc";
import { reauthenticateForSecondFactor } from "./stepUp";

// Session state for the SPA. Models "is there an authenticated session?" so the
// route guard, sign-out, and the 401 -> login redirect work for every feature (and
// for the E2E harness's expired-session simulation). The Keycloak tokens that back
// a real SSO session live separately in ./tokenStore (attached as Bearer by lib/api
// and carried by the SCORM transport); credential entry / TOTP / WebAuthn happen on
// the Keycloak-themed pages, and ./oidc drives the authorization-code + PKCE flow.
// A session can also exist without tokens — the external-learner email-link flow and
// the E2E harness both seed one directly — so token clearing is best-effort here.

export interface Session {
  /**
   * WHO is signed in, as the SIGN-IN PATH knows them — and **never** the backend's `users.id`. In the
   * OIDC flow this is the identity provider's subject (`claims.sub`, src/auth/oidc's
   * `sessionFromTokens`); the email-link flow stores `"external-learner"` and the no-IdP dev fallback
   * stores `"demo"`. None of the three is a database key, and the backend keeps the subject in
   * `users.keycloak_subject` precisely because it is not one (ADR-0013 §3.1).
   *
   * So this value belongs in `key=`, in a cache lookup, and nowhere in a request body that asks for a
   * user id: the server resolves the caller from the token it already validated. MJ-US-E1.1 shipped a
   * creation form that sent it as `leadConsultantUserId` and answered 422 — or 400, since a real subject
   * is not a uuid — for every consultant whose session came from a real login.
   */
  userId: string;
  displayName: string;
  tenantSlug: string | null;
  /**
   * Canonical role codes for the signed-in user (Keycloak realm roles; see
   * src/auth/capabilities.ts). Drives client-side nav gating and route guards —
   * the API still enforces authorization, so these only shape what the UI offers.
   * Seeded from the token in the OIDC flow, and directly by the email-link and E2E
   * paths (which carry no token); an empty array means "learner-only" access.
   */
  roles: string[];
  // NO membership roles here, and that is ADR-0021 §9. {@link roles} is the account's CLIENT roles, which
  // ADR-0018 §4.2 confines to its own tenant; a customer reached BY MEMBERSHIP replaces them with that
  // tenant's roles, and those belong to the SELECTION rather than to the session — the choice is made
  // after the token exists (§6.1) and the carrier is a `tenant_memberships` row that no token reflects
  // (§4.1). `auth/tenantSelection.ts` holds them, from the answer `GET /api/auth/memberships` gives (§5),
  // and `auth/capabilities.ts` is where the two meet. A session persisted while the retired
  // `membershipGroups` field existed simply carries one more key that nothing reads.
}

const SESSION_STORAGE_KEY = "clms.session";

interface SessionContextValue {
  session: Session | null;
  /**
   * True when the session ended because the backend answered 401 (expired
   * token/session), as opposed to never having signed in or an explicit
   * sign-out. Lets the route guard explain the redirect (reason=session_expired)
   * so the login page can surface the "your session expired" banner. Reset on the
   * next successful sign-in.
   */
  expired: boolean;
  /**
   * The slug of the organisation the session belongs to — and, once it has ended, the one it BELONGED to.
   *
   * It exists for the bounce out of the apex shell (app/RequireAuth.tsx, task 09e495b1). Production serves
   * every tenant from one apex host and the authenticated shell is apex-only (ADR-0018 §6.3), so a URL
   * inside the app names no tenant: when a session dies there, the only record of which organisation the
   * person was in is the session that is being cleared. Dropping it sent them to a bare `/login`, where the
   * email-link request they typed their address into answered "Organisaatiota ei löytynyt" — the
   * production report of 3.11.2026, reached from a mailed questionnaire link whose stored session had
   * since been revoked (app/TenantRespondEntry.tsx).
   *
   * Held in state rather than in storage, because it is a property of THIS document's history and not of
   * the browser: a tab that never had a session has nothing to name, and seeding it from storage on boot is
   * what makes a reload of the expired page keep the organisation.
   */
  lastTenantSlug: string | null;
  /** Persist a session (set by the OIDC callback and the email-link redeem). */
  setSession: (session: Session | null) => void;
  /** Clears the local session + tokens; used on 401 (expired session). No IdP round-trip. */
  clear: () => void;
  /** User-initiated sign-out: clears local state, then RP-initiated Keycloak logout when configured. */
  signOut: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function readStoredSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    // Tolerate sessions persisted before roles existed: default to learner-only.
    return { ...parsed, roles: parsed.roles ?? [] };
  } catch {
    return null;
  }
}

/**
 * The persisted session, read OUTSIDE React.
 *
 * `useSession` is how a component asks; this is for the plain functions a component calls, which cannot
 * hold a hook — `auth/stepUp`, which has to know which tenant the signed-in person belongs to. Reading the
 * same storage key the provider seeds itself from, so the two can never disagree.
 */
export function storedSession(): Session | null {
  return readStoredSession();
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<Session | null>(readStoredSession);
  const [expired, setExpired] = useState(false);
  // Seeded from storage so a document that BOOTS on a session already knows its organisation — the
  // expiry then has nothing left to find out (see `lastTenantSlug` on the context above).
  const [lastTenantSlug, setLastTenantSlug] = useState<string | null>(
    () => readStoredSession()?.tenantSlug ?? null,
  );

  const setSession = useCallback((next: Session | null) => {
    setSessionState(next);
    if (next) {
      setLastTenantSlug(next.tenantSlug);
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(next));
      // A fresh sign-in clears any prior expiry so the banner doesn't linger.
      setExpired(false);
    } else {
      // READ BEFORE THE REMOVE: the session on its way out is the only thing that still names the
      // organisation, and on the apex host there is no URL to recover it from afterwards. A session
      // that named none (the tenant-less consultant door) leaves the previous answer standing rather
      // than overwriting it with null.
      const ending = readStoredSession()?.tenantSlug ?? null;
      if (ending) setLastTenantSlug(ending);
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, []);

  // Local clear: drop the session and any Keycloak tokens without an IdP round-trip.
  // Used by sign-out and (via `expire`) by the 401 hook so the app falls back to /login.
  const clear = useCallback(() => {
    clearTokens();
    setSession(null);
  }, [setSession]);

  // 401 hook: mark the session expired *then* clear it, so the guard redirects to
  // /login?reason=session_expired and the login page shows the expiry banner. Kept
  // distinct from `clear`/`signOut`, which drop the session without the banner.
  const expire = useCallback(() => {
    setExpired(true);
    clear();
  }, [clear]);

  // User-initiated sign-out: end the Keycloak SSO session too (RP-initiated logout),
  // so a re-login doesn't silently reuse the IdP session. Clears local state first so
  // the app is signed out even if the redirect is blocked; falls back to a plain local
  // sign-out (RequireAuth then redirects to /login) when no IdP is configured.
  const signOut = useCallback(() => {
    const logoutUrl = buildLogoutUrl();
    clear();
    if (logoutUrl) window.location.assign(logoutUrl);
  }, [clear]);

  // A 401 from any API call expires the session everywhere at once.
  useEffect(() => {
    setSessionExpiredHandler(expire);
    return () => setSessionExpiredHandler(() => {});
  }, [expire]);

  // ADR-0021 §6.2 — a privileged session with no second factor, from any API call, drives ONE
  // re-authentication. Beside the 401 hook above because it is the same kind of thing: a response the
  // whole session has to answer, not one a page can. It is pointedly NOT `expire` — the session is
  // perfectly valid and clearing it would send the person to /login, where they would sign in
  // successfully and meet the identical refusal on the next call, for ever. The re-auth forces a fresh
  // credential prompt instead, which is where the realm asks for the factor (and enrols one).
  useEffect(() => {
    setSecondFactorRequiredHandler((tokenUsed) => {
      void reauthenticateForSecondFactor(tokenUsed);
    });
    return () => setSecondFactorRequiredHandler(() => {});
  }, []);

  const value = useMemo(
    () => ({ session, expired, lastTenantSlug, setSession, clear, signOut }),
    [session, expired, lastTenantSlug, setSession, clear, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- context + hook colocated by design
export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within a SessionProvider");
  return ctx;
}
